import {
  FC,
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';

import {
  useCompleteInitialConfig,
  useParseLocationQuery,
  useRequest,
  useInterval,
  useImportsOrdersDialogsState,
  useToggle,
  useSubscriptionFeatures,
} from '~hooks/index';
import {
  getDashboardCarrierStatitics,
  getZoneSaturation,
} from '~services/dashboard';
import {
  DashboardCarrierStatitics,
  DashboardZoneSaturationResponse,
} from '~services/dashboard/types';
import { PlanFeaturesTypes } from '~globals/types/enums';

import {
  DashboardVariantV2CarrierQueryParams,
  DashboardVariantV2CarrierContextValue,
  DashboardVariantV2CarrierContextTabId,
  DashboardVariantV2CarrierContextZoneSaturationReloadParams,
} from './types';

const DEFAULT_STATISTICS: DashboardCarrierStatitics = {
  companyInfo: {
    quantityVehicles: 0,
    quantityWarehouses: 0,
    quantityCarrierCompanies: 0,
    quantityGiverCompanies: 0,
  },
  totalItems: 0,
  collect: {
    totalCollectedItems: 0,
    totalCollectItems: 0,
    totalToCollectItems: 0,
  },
  inWarehouse: {
    totalDelayedInWarehouseItems: 0,
    totalInWarehouseItems: 0,
    totalNextDaysInWarehouseItems: 0,
    totalTodayInWarehouseItems: 0,
  },
  inProgress: {
    totalInProgressItems: 0,
    totalInProgressItemsFlex: 0,
    totalInProgressItemsNoFlex: 0,
    totalItemsInProgress: 0,
    totalItemsScheduled: 0,
  },
  completed: {
    totalCompletedtems: 0,
    totalWithIssuesItems: 0,
    totalWithoutIssuesItems: 0,
  },
  route: {
    totalCompletedRoutes: 0,
    totalInProgressRoutes: 0,
    totalPendingRoutes: 0,
    totalRoutes: 0,
  },
};

const DEFAULT_ZONE_SATURATION: DashboardZoneSaturationResponse = {
  zones: [],
  totalDriversCount: 0,
  totalOnTheWayItems: 0,
  totalInWarehouseItems: 0,
  totalInTransitItems: 0,
  totalDemand: 0,
  totalCapacity: 0,
  totalOccupancyPercentage: 0,
  totalSaturatedZones: 0,
  totalOnAlertZones: 0,
  totalWithMarginZones: 0,
};

const DEFAULT_INTERVAL_RELOAD_STATISTICS = 15000; //@TODO: UI V2: Verify

const DashboardVariantV2CarrierContext =
  createContext<DashboardVariantV2CarrierContextValue>({
    data: {
      statistics: {
        initialLoading: true,
        loading: true,
        data: DEFAULT_STATISTICS,
        reload: () => {},
      },
      zoneSaturation: {
        initialLoading: false,
        loading: false,
        data: DEFAULT_ZONE_SATURATION,
        reload: () => {},
        expand: {
          value: false,
          toggleValue: () => {},
        },
      },
      reloadDataByConfig: () => {},
    },
    activeTab: {
      currentTab: 'inTransit',
      setActiveTab: () => {},
      getIsActiveTab: () => false,
    },
    dialogs: {
      importFromFile: { open: false, toggleOpen: () => {} },
      importFromIntegration: { open: false, toggleOpen: () => {} },
      importFromAssociatedCompany: { open: false, toggleOpen: () => {} },
      updateGeoError: { open: false, toggleOpen: () => {} },
    },
    ignoreRefreshBySocket: {
      value: false,
      toggleValue: () => {},
    },
    expandedHasChangeOrdersAlert: {
      value: false,
      toggleValue: () => {},
    },
    modifiedItemsIdBySocket: {
      value: [],
      setValue: () => {},
    },
  });

export const useDashboardVariantV2CarrierContext =
  (): DashboardVariantV2CarrierContextValue =>
    useContext(DashboardVariantV2CarrierContext);

const DashboardVariantV2CarrierContextProvider: FC = ({ children }) => {
  const { setCompletedConfig, handleOpenMenuFirstStepOnCompleted } =
    useCompleteInitialConfig();

  const queryParams =
    useParseLocationQuery<DashboardVariantV2CarrierQueryParams>();

  const [activeTab, setActiveTab] =
    useState<DashboardVariantV2CarrierContextTabId>('inTransit');

  const getIsActiveTab = useCallback(
    (currentTab: DashboardVariantV2CarrierContextTabId) =>
      activeTab === currentTab,
    [activeTab],
  );

  const { hasAccessToFeature } = useSubscriptionFeatures();
  const hasAccessMgmZones = useMemo(
    () => hasAccessToFeature(PlanFeaturesTypes.MgmZones),
    [hasAccessToFeature],
  );

  const [statistics, setStatistics] =
    useState<DashboardCarrierStatitics>(DEFAULT_STATISTICS);
  const [initialLoadingStatistics, setInitialLoadingStatistics] =
    useState(true);
  const [isFirstRequestStatistics, setIsFirstRequestStatistics] =
    useState(true);
  const [, loadingStatistics, , reloadStatisticsRequest] = useRequest(
    {
      request: getDashboardCarrierStatitics,
      payload: null,
      withPostSuccess: (response) => {
        const currentStatistics = response.data
          ?.data as DashboardCarrierStatitics;

        setStatistics(currentStatistics);

        if (
          isFirstRequestStatistics &&
          currentStatistics.route.totalInProgressRoutes === 0
        ) {
          setActiveTab('inWarehouse');
        }

        handleOpenMenuFirstStepOnCompleted();
      },
      withPostFetch: () => {
        setInitialLoadingStatistics(false);
        setIsFirstRequestStatistics(false);
      },
    },
    [],
  );
  const reloadStatistics = useCallback(() => {
    reloadStatisticsRequest(null);
  }, [reloadStatisticsRequest]);

  const [zoneSaturation, setZoneSaturation] =
    useState<DashboardZoneSaturationResponse>(DEFAULT_ZONE_SATURATION);
  const [initialLoadingZoneSaturation, setInitaLoadingZoneSaturation] =
    useState(() => hasAccessMgmZones);
  const [loadingZoneSaturation, setLoadingZoneSaturation] = useState(
    () => hasAccessMgmZones,
  );
  const [, , , reloadZoneSaturationRequest] = useRequest(
    {
      request: getZoneSaturation,
      initialLoading: hasAccessMgmZones,
      isValidToRequest: hasAccessMgmZones,
      payload: { onlyTodayDelivery: false },
      withPostSuccess: (response) => {
        const currentZoneSaturation = response.data
          ?.data as DashboardZoneSaturationResponse;

        setZoneSaturation(currentZoneSaturation);
      },
      withPostFetch: () => {
        setInitaLoadingZoneSaturation(false);
        setLoadingZoneSaturation(false);
      },
    },
    [],
  );
  const reloadZoneSaturation = useCallback(
    ({
      withLoading,
      onlyTodayDelivery,
    }: DashboardVariantV2CarrierContextZoneSaturationReloadParams) => {
      if (hasAccessMgmZones) {
        if (withLoading) setLoadingZoneSaturation(true);

        reloadZoneSaturationRequest({ onlyTodayDelivery });
      }
    },
    [hasAccessMgmZones, reloadZoneSaturationRequest],
  );
  const [expandZoneSaturation, toggleExpandZoneSaturation] = useToggle(false);

  const reloadDataByConfig = useCallback(
    (isCompleteConfig) => {
      setCompletedConfig(isCompleteConfig);
      reloadStatistics();
    },
    [setCompletedConfig, reloadStatistics],
  );

  useInterval(
    () => reloadDataByConfig(false),
    DEFAULT_INTERVAL_RELOAD_STATISTICS,
  );

  const {
    importFromFile: dialogImportFromFileToggleState,
    importFromIntegration: dialogImportFromIntegrationToggleState,
    importFromAssociatedCompany: dialogImportFromAssociatedCompanyToggleState,
  } = useImportsOrdersDialogsState({
    defaultOpenDialogImportFromFile: queryParams?.openImporterFromFile,
    defaultOpenDialogImportFromIntegration:
      queryParams?.openImporterFromIntegration,
    defaultOpenImportFromAssociatedCompany:
      queryParams?.openImporterFromAssociatedCompany,
  });

  const [openDialogUpdateGeoError, toogleOpenDialogUpdateGeoError] =
    useToggle(false);

  const [ignoreRefreshBySocket, toggleIgnoreRefreshBySocket] = useToggle(false);

  const [expandedHasChangeOrdersAlert, toggleExpandedHasChangeOrdersAlert] =
    useToggle(false);

  const [modifiedItemsIdBySocket, setModifiedItemsIdBySocket] = useState<
    string[]
  >([]);

  return (
    <DashboardVariantV2CarrierContext.Provider
      value={{
        data: {
          statistics: {
            initialLoading: initialLoadingStatistics,
            loading: loadingStatistics,
            data: statistics,
            reload: reloadStatistics,
          },
          zoneSaturation: {
            initialLoading: initialLoadingZoneSaturation,
            loading: loadingZoneSaturation,
            data: zoneSaturation,
            reload: reloadZoneSaturation,
            expand: {
              value: expandZoneSaturation,
              toggleValue: toggleExpandZoneSaturation,
            },
          },
          reloadDataByConfig,
        },
        activeTab: {
          currentTab: activeTab,
          setActiveTab,
          getIsActiveTab,
        },
        dialogs: {
          importFromFile: dialogImportFromFileToggleState,
          importFromIntegration: dialogImportFromIntegrationToggleState,
          importFromAssociatedCompany:
            dialogImportFromAssociatedCompanyToggleState,
          updateGeoError: {
            open: openDialogUpdateGeoError,
            toggleOpen: toogleOpenDialogUpdateGeoError,
          },
        },
        ignoreRefreshBySocket: {
          value: ignoreRefreshBySocket,
          toggleValue: toggleIgnoreRefreshBySocket,
        },
        expandedHasChangeOrdersAlert: {
          value: expandedHasChangeOrdersAlert,
          toggleValue: toggleExpandedHasChangeOrdersAlert,
        },
        modifiedItemsIdBySocket: {
          value: modifiedItemsIdBySocket,
          setValue: setModifiedItemsIdBySocket,
        },
      }}
    >
      {children}
    </DashboardVariantV2CarrierContext.Provider>
  );
};

export default DashboardVariantV2CarrierContextProvider;
